import { nanoid } from "nanoid";
import { z } from "zod";
import { BLOCK_REGISTRY, blockDef } from "./blocks/registry";
import { StylePropsSchema, StyleSheetSchema, type StyleProps, type StyleSheet } from "./styles";

/**
 * Version of the email document schema. Bump when the shape changes and add a
 * migration in ./migrations.
 */
export const CURRENT_SCHEMA_VERSION = 1;

export function newNodeId(): string {
    return nanoid(10);
}

export interface DocNode {
    /** Stable across edits — undo history, css classes and warnings key off it. */
    id: string;
    type: string;
    attrs: Record<string, unknown>;
    styles: StyleProps;
    mobileStyles: StyleProps;
    /** Template-locked region: no move/delete, restricted editing. */
    locked?: boolean;
    children?: DocNode[];
}

export interface EmailDocument {
    schemaVersion: number;
    meta: { title: string; subject: string; preheader: string };
    settings: {
        contentWidth: number;
        backgroundColor: string;
        fontFamilies: string[];
        language: string;
        direction: "ltr" | "rtl";
        brandKitId?: string;
    };
    /** Template-layer style overrides per block type. */
    styles: StyleSheet;
    /** Template-layer mobile overrides per block type. */
    mobileStyles: StyleSheet;
    root: DocNode;
}

const NodeIdSchema = z.string().min(4).max(32);

/**
 * Structural node validation (shape + registry membership + child rules +
 * per-type attrs). Implemented imperatively so errors carry node paths.
 */
function validateNode(
    node: unknown,
    path: string,
    issues: string[],
    seenIds: Set<string>,
    expectedType?: string,
): void {
    const n = node as Partial<DocNode>;
    if (typeof n !== "object" || n === null) {
        issues.push(`${path}: not an object`);
        return;
    }
    if (!NodeIdSchema.safeParse(n.id).success) {
        issues.push(`${path}: invalid node id`);
        return;
    }
    if (seenIds.has(n.id!)) issues.push(`${path}: duplicate node id ${n.id}`);
    seenIds.add(n.id!);

    if (typeof n.type !== "string" || !BLOCK_REGISTRY.has(n.type)) {
        issues.push(`${path}: unknown block type ${String(n.type)}`);
        return;
    }
    if (expectedType && n.type !== expectedType) {
        issues.push(`${path}: expected ${expectedType}, got ${n.type}`);
    }

    const def = blockDef(n.type);
    const attrsResult = def.attrsSchema.safeParse(n.attrs ?? {});
    if (!attrsResult.success) {
        issues.push(
            `${path}: invalid attrs for ${n.type}: ${attrsResult.error.issues
                .map((i) => `${i.path.join(".")} ${i.message}`)
                .join("; ")}`,
        );
    }
    for (const which of ["styles", "mobileStyles"] as const) {
        const res = StylePropsSchema.safeParse(n[which] ?? {});
        if (!res.success) issues.push(`${path}: invalid ${which}`);
    }

    const children = n.children;
    if (def.allowedChildren.length === 0) {
        if (children && children.length > 0)
            issues.push(`${path}: ${n.type} cannot have children`);
        return;
    }
    if (!Array.isArray(children)) {
        issues.push(`${path}: ${n.type} requires a children array`);
        return;
    }
    children.forEach((child, i) => {
        const childType = (child as DocNode)?.type;
        if (typeof childType === "string" && !def.allowedChildren.includes(childType)) {
            issues.push(`${path}.children[${i}]: ${childType} not allowed inside ${n.type}`);
        }
        validateNode(child, `${path}.children[${i}]`, issues, seenIds);
    });
}

const DocumentShapeSchema = z.object({
    schemaVersion: z.number().int().positive(),
    meta: z
        .object({
            title: z.string().max(300),
            subject: z.string().max(500),
            preheader: z.string().max(500),
        })
        .strict(),
    settings: z
        .object({
            contentWidth: z.number().int().min(320).max(900),
            backgroundColor: z.string(),
            fontFamilies: z.array(z.string()).min(1),
            language: z.string().min(2).max(10),
            direction: z.enum(["ltr", "rtl"]),
            brandKitId: z.string().optional(),
        })
        .strict(),
    styles: StyleSheetSchema,
    mobileStyles: StyleSheetSchema,
    root: z.unknown(),
});

export interface ParseResult {
    ok: boolean;
    document?: EmailDocument;
    issues: string[];
}

/** Full document validation. Returns issues rather than throwing. */
export function parseEmailDocument(raw: unknown): ParseResult {
    const shape = DocumentShapeSchema.safeParse(raw);
    if (!shape.success) {
        return {
            ok: false,
            issues: shape.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`),
        };
    }
    if (shape.data.schemaVersion !== CURRENT_SCHEMA_VERSION) {
        return {
            ok: false,
            issues: [
                `schemaVersion ${shape.data.schemaVersion} != current ${CURRENT_SCHEMA_VERSION} (run migrateDocument first)`,
            ],
        };
    }
    const issues: string[] = [];
    validateNode(shape.data.root, "root", issues, new Set(), "root");
    if (issues.length > 0) return { ok: false, issues };
    return { ok: true, document: shape.data as EmailDocument, issues: [] };
}

/** Validate a standalone node subtree (saved blocks). */
export function parseBlockSubtree(raw: unknown): { ok: boolean; node?: DocNode; issues: string[] } {
    const issues: string[] = [];
    validateNode(raw, "block", issues, new Set());
    if (issues.length > 0) return { ok: false, issues };
    return { ok: true, node: raw as DocNode, issues: [] };
}

/** Collect ids of nodes marked locked (template locked regions). */
export function collectLockedIds(root: DocNode): string[] {
    const out: string[] = [];
    walkNodes(root, (n) => {
        if (n.locked) out.push(n.id);
    });
    return out;
}

export function assertEmailDocument(raw: unknown): EmailDocument {
    const res = parseEmailDocument(raw);
    if (!res.ok || !res.document) {
        throw new Error(`Invalid email document:\n${res.issues.join("\n")}`);
    }
    return res.document;
}

// ---------------------------------------------------------------------------
// Factories & tree helpers
// ---------------------------------------------------------------------------

export function createNode(type: string, overrides?: Partial<DocNode>): DocNode {
    const def = blockDef(type);
    const node: DocNode = {
        id: newNodeId(),
        type,
        attrs: def.defaultAttrs(),
        styles: {},
        mobileStyles: {},
        ...overrides,
    };
    if (def.allowedChildren.length > 0 && !node.children) node.children = [];
    return node;
}

/** A section containing one row with the given column ratios. */
export function createSection(ratios: number[] = [1]): DocNode {
    const row = createNode("row");
    row.attrs = { ...row.attrs, columnRatios: ratios };
    row.children = ratios.map(() => createNode("column"));
    const section = createNode("section");
    section.children = [row];
    return section;
}

export function createEmptyDocument(title = "Untitled email"): EmailDocument {
    return {
        schemaVersion: CURRENT_SCHEMA_VERSION,
        meta: { title, subject: "", preheader: "" },
        settings: {
            contentWidth: 600,
            backgroundColor: "#f4f4f4",
            fontFamilies: ["Helvetica", "Arial", "sans-serif"],
            language: "en",
            direction: "ltr",
        },
        styles: {},
        mobileStyles: {},
        root: { id: newNodeId(), type: "root", attrs: {}, styles: {}, mobileStyles: {}, children: [] },
    };
}

export function findNode(root: DocNode, id: string): DocNode | null {
    if (root.id === id) return root;
    for (const child of root.children ?? []) {
        const hit = findNode(child, id);
        if (hit) return hit;
    }
    return null;
}

export function walkNodes(root: DocNode, visit: (node: DocNode, parent: DocNode | null) => void): void {
    const walk = (node: DocNode, parent: DocNode | null) => {
        visit(node, parent);
        for (const child of node.children ?? []) walk(child, node);
    };
    walk(root, null);
}

/** Deep-clone a subtree assigning fresh ids (saved-block insertion). */
export function cloneWithNewIds(node: DocNode): DocNode {
    return {
        ...node,
        id: newNodeId(),
        attrs: structuredClone(node.attrs),
        styles: { ...node.styles },
        mobileStyles: { ...node.mobileStyles },
        children: node.children?.map(cloneWithNewIds),
    };
}
